/**
 * 维度链选中态切片 · AI 智能分类 v6
 *
 * 仅持有 "当前活跃 session 的轻量摘要" 这一个全局概念:
 *   · sessionId / chainVersion (会话句柄, postConfirm / postSample 等需要)
 *   · expandedSize / layerCount / layersSummary (维度链摘要, 给 dimension_updated
 *     handler 写入用; 顶层 chain 摘要展示也可读取)
 *   · chainStatus (会话状态: active / awaiting_choice, awaiting_choice 时抑制
 *     某些操作)
 *
 * 用户反选 (excludedKeys) 不再放本 slice — v6 重构后每条 folder_plan card 持
 * 有自己的反选状态 (local state + card.excludedKeys hydrate), 跟 history 同
 * 构, 单例化前缀消失。
 *
 * "本会话已发起建夹"信号也不在这里 — 由各条 folder_plan card 的 taskCreated
 * 字段表达 (hydrate 接口 5 时回填)。
 *
 * 数据来源:
 *   · setClassifyPreview  ← SSE dimension_updated 事件 (ApplyDimensionOp 成功后)
 *
 * 与旧 uiSlice.selectedFolderIds 并存 (老 v5 final_proposal 灰度兼容期)。
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { LayerSummary } from '../../types/ai-classify'
import { conversationCleared, teamSwitched } from './sessionSlice'

interface SelectionState {
  /** 当前活跃 session_id · null 表示本会话还没有维度链 */
  sessionId: string | null
  /** 维度链版本 · 每次 layer CRUD 自增，前端 preview 接口 ETag 用 */
  chainVersion: number
  /** expand 后的叶子文件夹数（服务端缓存值） */
  expandedSize: number
  /** 当前维度链层数 */
  layerCount: number
  /** 维度链摘要（每层 dimension/scope/value_count，不含具体取值） */
  layersSummary: LayerSummary[]
  /**
   * 后端 session.status 镜像 (active / awaiting_choice 这两个 chat 阶段相关的)。
   *
   * awaiting_choice 时:
   *   · FolderPlanPreview 自动 return null（让选择和方案互斥）
   *   · "确认创建"按钮禁用（兜底, 后端 confirm 也会拒）
   *   · 用户答完选项 LLM 出 dimension_op → 后端切回 active 推 dimension_updated → 此处也切回
   *
   * 默认 'active' (兼容老流程无此字段)。
   */
  chainStatus: 'active' | 'awaiting_choice'
}

const initialState: SelectionState = {
  sessionId: null,
  chainVersion: 0,
  expandedSize: 0,
  layerCount: 0,
  layersSummary: [],
  chainStatus: 'active'
}

const selectionSlice = createSlice({
  name: 'aiClassify/selection',
  initialState,
  reducers: {
    /**
     * 写入分类预览数据 · SSE dimension_updated 事件或 init 恢复会话时触发。
     */
    setClassifyPreview(
      state,
      action: PayloadAction<{
        sessionId: string
        chainVersion: number
        expandedSize: number
        layerCount: number
        layersSummary: LayerSummary[]
      }>
    ) {
      const p = action.payload
      state.sessionId = p.sessionId
      state.chainVersion = p.chainVersion
      state.expandedSize = p.expandedSize
      state.layerCount = p.layerCount
      state.layersSummary = p.layersSummary
      // dimension_updated 必然意味着后端 session.status=active（出了新 layer = 答完选项了）
      state.chainStatus = 'active'
    },
    /**
     * 后端 session.status 切到 awaiting_choice (LLM 出 ask/next_level/delete_confirm)。
     * SSE 'final' 事件 payload.session_status='awaiting_choice' 时 dispatch.
     */
    sessionStatusUpdated(
      state,
      action: PayloadAction<{
        sessionId: string
        status: 'active' | 'awaiting_choice'
      }>
    ) {
      const p = action.payload
      if (state.sessionId === p.sessionId || !state.sessionId) {
        state.chainStatus = p.status
      }
    },
    /** 重置为初始态 · 新建会话 / 放弃当前会话 */
    selectionReset() {
      return initialState
    }
  },
  // 与 messagesSlice / uiSlice / taskSlice 一致: 监听会话切换 / 团队切换 联动清空。
  //
  // bad case 2026-05-15 (用户截图 10): 切换 conversation 时 selectionSlice
  // 没接联动清空 → 老 conversation 的 chain 摘要继续显示在新 conversation
  // 的 FolderPlanPreview 里 (如「关键词:罗永浩」窜到「按月份分」对话)。
  //
  // 修复后行为:
  //   · 同一 session 内 dimension_op 更新 → 正常覆盖 layersSummary (用户能看到最新链)
  //   · 切 conversation / 新对话 → conversationCleared 触发, chainSessionId /
  //     layersSummary 等全清空; 新 conv 的 dimension_updated SSE 进来后才会重建
  //   · 切团队 → teamSwitched 触发, 同上行为
  //   · 历史 conversation 当时的 plan 不会被新 conv 替换 (维度链恢复方案另行处理 · A4 降级)
  extraReducers: (builder) => {
    builder
      .addCase(conversationCleared, () => initialState)
      .addCase(teamSwitched, () => initialState)
  }
})

export const { setClassifyPreview, sessionStatusUpdated, selectionReset } =
  selectionSlice.actions

export default selectionSlice.reducer

// ---- selectors（从根 store 的 aiClassify.selection 子树读）----

type SelectionRoot = { aiClassify: { selection: SelectionState } }

export const selectChainSessionId = (s: SelectionRoot): string | null =>
  s.aiClassify.selection.sessionId
export const selectChainVersion = (s: SelectionRoot): number =>
  s.aiClassify.selection.chainVersion
export const selectChainExpandedSize = (s: SelectionRoot): number =>
  s.aiClassify.selection.expandedSize
export const selectChainLayersSummary = (s: SelectionRoot): LayerSummary[] =>
  s.aiClassify.selection.layersSummary
export const selectChainStatus = (
  s: SelectionRoot
): 'active' | 'awaiting_choice' => s.aiClassify.selection.chainStatus
